import { FC, useMemo } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import clsx from "clsx";

import TextFieldFactory from "shared/ui/user-input/text-field";
import Modal, { ModalChildrenFnPropsI } from "shared/ui/modal";
import { getEmailsFromString, yupEmailListSchema } from "shared/lib/helpers";
import { APII, glencocoClientAPI } from "@/api/glencoco";
import { useApi } from "shared/lib/hooks/use-api";
import { ReplyAccountContactEmailRequestParamsI } from "@/api/routes/account/interfaces";
import { EmailEditor } from "shared/ui/email/editor";

/**
 * Email may be passed either as a pure email string or in "Name Surname <hello@world.com>"
 * format, we need to extract it in a pure format.
 *
 * @param rawEmail
 */
const extractEmail = (rawEmail: string | null | undefined) => {
  if (!rawEmail) {
    return null;
  }

  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
  const match = rawEmail.match(emailRegex);

  return match ? match[0] : null;
};

export interface AccountReplyEmailModalPropsI {
  replyToEmail?: string;
  emailId?: string;
  onSuccess: () => void;
  onClose?: () => void;
}

interface AccountSendEmailFormValuesI {
  emailsRaw: string;
  replyRaw: string;
  replyHTML: string;
}

export const ACCOUNT_REPLY_EMAIL_MODAL_ID = "account-reply-email-modal";

const Input = TextFieldFactory(Field);

const sendAccountContactEmailValidationSchema = Yup.object().shape({
  emailsRaw: yupEmailListSchema,
  replyRaw: Yup.string().required("Email body is required"),
  replyHTML: Yup.string(),
});

const replyAccountContactEmailFetcher = (
  api: APII,
  emailId: string,
  request: ReplyAccountContactEmailRequestParamsI
) => api.replyAccountContactEmail(emailId, request);

const AccountReplyEmailModalContent: FC<
  ModalChildrenFnPropsI & AccountReplyEmailModalPropsI
> = ({ handleClose, onSuccess, replyToEmail, emailId }) => {
  const [, requestReplyEmail] = useApi({
    apiFactory: glencocoClientAPI,
    shouldCallAutomatically: false,
    fetcher: replyAccountContactEmailFetcher,
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
    showToastOnSuccess: true,
    successBuilder: () => "Email reply has been requested successfully!",
    errorBuilder: (response) =>
      response.status === 401
        ? "The prospect requested not to email"
        : "Failed to reply to email. Please try again later.",
    onError: handleClose,
  });

  const preparedReplyToEmail = useMemo(
    () => extractEmail(replyToEmail),
    [replyToEmail]
  );

  const formInitialValues: AccountSendEmailFormValuesI = useMemo(
    () => ({
      emailsRaw: preparedReplyToEmail || "",
      replyRaw: "",
      replyHTML: "",
    }),
    [preparedReplyToEmail]
  );

  const handleSubmit = ({
    emailsRaw,
    replyRaw,
    replyHTML,
  }: AccountSendEmailFormValuesI) => {
    if (!emailId || !preparedReplyToEmail) {
      toast.error(
        "Email ID and reply-to email address are required to send an email."
      );

      return;
    }

    // console.debug(replyRaw, replyRaw.length);
    return requestReplyEmail(emailId, {
      email_recipients: getEmailsFromString(emailsRaw),
      email_body: replyRaw,
      email_html_body: replyHTML,
    });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      validationSchema={sendAccountContactEmailValidationSchema}
    >
      {({ isSubmitting, isValid, errors, touched, setValues, values }) => (
        <Form className="flex flex-col">
          <Input
            name="emailsRaw"
            label="Send Reply Email(s) To (comma-separated):"
            placeholder="Enter email addresses for a reply email to be sent"
            errors={errors.emailsRaw}
            touched={touched.emailsRaw}
          />

          <div>
            <span className="pl-1 typography-body-4-bold">Email body</span>
            <EmailEditor
              className="mb-4 mt-2 border"
              initialContent={undefined}
              placeholder="Share any notes you have about what to put in the email"
              onChange={async ({ plainText, htmlText }) => {
                await setValues({
                  ...values,
                  replyRaw: plainText || "",
                  replyHTML: htmlText || "",
                });
              }}
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" className="btn-nofill" onClick={handleClose}>
              Cancel
            </button>

            <button
              type="submit"
              className={clsx("btn-ae-default w-[150px]", {
                disabled: isSubmitting || !isValid,
                loading: isSubmitting,
              })}
            >
              Send
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export const AccountReplyEmailModal: FC<AccountReplyEmailModalPropsI> = (
  props
) => (
  <Modal
    id={ACCOUNT_REPLY_EMAIL_MODAL_ID}
    title="Reply to Email"
    onClose={props.onClose}
    modalContainerClassName="max-h-[500px]"
  >
    {(modalContentProps) => (
      <AccountReplyEmailModalContent {...modalContentProps} {...props} />
    )}
  </Modal>
);
